'use client';

import {
  ActionIcon,
  Box,
  Button,
  Container,
  Group,
  Loader,
  Menu,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCheck, IconSettings, IconX } from '@tabler/icons-react';
import { useEffect } from 'react';

import { ReleaseNotesContent } from '@/components/release-notes/release-notes-content';

import type { ReleaseNotesPreference, VersionContent } from '@/typings/version';

type ReleaseNotesModalProps = {
  loading: boolean;
  preference: ReleaseNotesPreference;
  latestVersion: VersionContent | null;
  otherVersions: VersionContent[];
  showOtherVersions: boolean;
  onPreferenceChange: (preference: ReleaseNotesPreference) => void;
  onShowOtherVersionsChange: (show: boolean) => void;
  onMarkAsRead: () => void;
  onClose: () => void;
};

export const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({
  loading,
  onClose,
  onPreferenceChange,
  onShowOtherVersionsChange,
  onMarkAsRead,
  latestVersion,
  otherVersions,
  showOtherVersions,
  preference,
}) => {
  // Mark as read when modal opens
  useEffect(() => {
    onMarkAsRead();
  }, []);

  const handlePreferenceChange = (value: ReleaseNotesPreference) => {
    onPreferenceChange(value);
  };

  const handleLoadOtherVersions = () => {
    onShowOtherVersionsChange(true);
  };

  const versionsToDisplay = showOtherVersions
    ? latestVersion
      ? [latestVersion, ...otherVersions]
      : otherVersions
    : latestVersion
      ? [latestVersion]
      : [];

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  return (
    <Modal
      opened
      onClose={onClose}
      size="xl"
      fullScreen={isMobile}
      padding={0}
      withCloseButton={false}
      styles={{
        body: { padding: 0 },
        content: { overflow: 'hidden' },
      }}
    >
      <Box>
        {/* Header with theme gradient */}
        <Box className="relative flex h-[150px] items-center bg-linear-to-br from-primary-500 to-primary-700 px-6 py-12">
          {/* Background pattern */}
          <Box
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />

          <Stack gap="xs" justify="space-between" align="flex-start" w="100%">
            <Group gap="md" mb="xs" w="100%">
              <Title order={1} c="white" fw={700} size="h2" flex={1}>
                Notas de Versão
              </Title>

              {/* Settings menu */}
              <Menu position="bottom-end" shadow="md" width={320}>
                <Menu.Target>
                  <ActionIcon variant="subtle" size="lg" c="white" aria-label="Configurações">
                    <IconSettings size={24} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  <Menu.Label fw={600} mb="xs">
                    Preferências de notificação
                  </Menu.Label>

                  <Menu.Item
                    onClick={() => handlePreferenceChange('default')}
                    styles={{
                      item: {
                        marginBottom: '8px',
                        '&:hover': {
                          backgroundColor: 'var(--mantine-color-primary-light)',
                        },
                      },
                      itemLabel: {
                        fontWeight: 500,
                      },
                    }}
                  >
                    <Box>
                      <Group gap={4} mb={2}>
                        <Text size="sm" fw={500}>
                          Exibir indicador
                        </Text>
                        {preference === 'default' && (
                          <IconCheck size={14} stroke={3} color="var(--mantine-color-primary-6)" />
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        Um indicador aparecerá na barra lateral quando houver novas atualizações
                      </Text>
                    </Box>
                  </Menu.Item>

                  <Menu.Item
                    onClick={() => handlePreferenceChange('immediate')}
                    styles={{
                      item: {
                        marginBottom: '8px',
                        '&:hover': {
                          backgroundColor: 'var(--mantine-color-primary-light)',
                        },
                      },
                      itemLabel: {
                        fontWeight: 500,
                      },
                    }}
                  >
                    <Box>
                      <Group gap={4} mb={2}>
                        <Text size="sm" fw={500}>
                          Abrir automaticamente
                        </Text>
                        {preference === 'immediate' && (
                          <IconCheck size={14} stroke={3} color="var(--mantine-color-primary-6)" />
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        Você recebe as notas assim que abrir o sistema após uma atualização
                      </Text>
                    </Box>
                  </Menu.Item>

                  <Menu.Item
                    onClick={() => handlePreferenceChange('never')}
                    styles={{
                      item: {
                        '&:hover': {
                          backgroundColor: 'var(--mantine-color-primary-light)',
                        },
                      },
                      itemLabel: {
                        fontWeight: 500,
                      },
                    }}
                  >
                    <Box>
                      <Group gap={4} mb={2}>
                        <Text size="sm" fw={500}>
                          Não notificar
                        </Text>
                        {preference === 'never' && (
                          <IconCheck size={14} stroke={3} color="var(--mantine-color-primary-6)" />
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        Você não receberá avisos sobre novas atualizações
                      </Text>
                    </Box>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              {/* Close button */}
              <ActionIcon
                onClick={onClose}
                variant="subtle"
                size="lg"
                c="white"
                aria-label="Fechar"
              >
                <IconX size={24} />
              </ActionIcon>
            </Group>
            <Text c="white" size="sm" opacity={0.9} maw={600}>
              Confira as novidades e melhorias implementadas em cada atualização do sistema
            </Text>
          </Stack>
        </Box>

        {/* Content */}
        <ScrollArea
          scrollbars="y"
          classNames={{
            root: 'bg-white',
            viewport: 'h-[calc(100vh-150px)] sm:h-[calc(90vh_-_150px)] p-4 md:p-6 pb-8',
          }}
        >
          {loading ? (
            <Loader />
          ) : (
            <Container size="lg">
              <Stack gap="xl">
                {/* Versions */}
                <ReleaseNotesContent versions={versionsToDisplay} />

                {/* Load more button */}
                {!showOtherVersions && otherVersions.length > 0 && (
                  <Box className="mt-8 text-center">
                    <Button onClick={handleLoadOtherVersions} variant="light" size="md">
                      Carregar versões anteriores
                    </Button>
                  </Box>
                )}
              </Stack>
            </Container>
          )}
        </ScrollArea>
      </Box>
    </Modal>
  );
};
